import type { RefObject } from 'react';
import { useEffect } from 'react';

const TABBABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** JSDOM often reports 0×0 for buttons; still treat them as tabbable. */
function isVisible(el: HTMLElement): boolean {
  const w = el.offsetWidth;
  const h = el.offsetHeight;
  if (w > 0 || h > 0) return true;
  if (el.tagName === 'BUTTON' || el.tagName === 'INPUT' || el.tagName === 'SELECT') return true;
  return !!(el.getClientRects?.().length);
}

/**
 * Keeps keyboard focus inside `containerRef` while `active` is true.
 * - Optional `initialFocusRef`: focused on mount when inside the container.
 * - Tab / Shift+Tab wrap between first and last tabbable descendants.
 */
export function useFocusTrap(
  containerRef: RefObject<HTMLElement | null>,
  active: boolean,
  options?: { initialFocusRef?: RefObject<HTMLElement | null> }
): void {
  const initialFocusRef = options?.initialFocusRef;

  useEffect(() => {
    if (!active) return;
    const root = containerRef.current;
    if (!root) return;

    const tabbables = (): HTMLElement[] =>
      Array.from(root.querySelectorAll<HTMLElement>(TABBABLE_SELECTOR)).filter(isVisible);

    const t = window.setTimeout(() => {
      if (initialFocusRef?.current && root.contains(initialFocusRef.current)) {
        initialFocusRef.current.focus();
      } else {
        tabbables()[0]?.focus();
      }
    }, 0);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const nodes = tabbables();
      if (nodes.length === 0) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      const cur = document.activeElement as HTMLElement | null;
      if (!e.shiftKey) {
        if (cur === last) {
          e.preventDefault();
          first.focus();
        }
      } else {
        if (cur === first) {
          e.preventDefault();
          last.focus();
        }
      }
    };

    root.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(t);
      root.removeEventListener('keydown', onKeyDown);
    };
  }, [active, containerRef, initialFocusRef]);
}
